/**
 * Recovery from stale-bundle chunk failures after a deploy.
 *
 * Every bundle filename is content-hashed, so a deploy renames all of them and
 * the previous build's files stop existing. A browser tab that was open across
 * the deploy is still running the old JavaScript, and the routes in App.js are
 * `React.lazy(() => import(...))` -- so the next navigation asks the server for
 * a chunk from a build that is gone. The fetch 404s, the lazy import rejects,
 * and with nothing catching it React unmounts the tree: a white page and no
 * message. That is what happened on 2026-08-07 when the FR-19 deploy landed
 * while the project page was open.
 *
 * The tab is not broken, it is merely out of date, and the fix is a reload.
 * Doing it automatically is safe because index.html is served `no-cache`, so a
 * reload revalidates and picks up the current build.
 *
 * The one hazard is a reload loop: if the chunk is missing for some reason a
 * reload cannot fix, reloading again just fails again, forever. So a reload is
 * attempted at most once per RELOAD_WINDOW_MS; a second failure inside that
 * window falls through to the ErrorBoundary, which tells the user plainly
 * rather than spinning.
 */

const RELOAD_KEY = 'scribe:chunk-reload-at';
const RELOAD_WINDOW_MS = 30000;

/**
 * Whether an error is a failed dynamic import rather than an application bug.
 *
 * The message differs by bundler and browser, so match several spellings.
 * Being liberal here is the safer direction: the cost of a false positive is
 * one reload, the cost of a false negative is the blank page this exists to
 * prevent.
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  const name = error.name || '';
  const message = error.message || String(error);

  return (
    name === 'ChunkLoadError'
    || /Loading chunk [\w-]+ failed/i.test(message)
    || /Loading CSS chunk [\w-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
  );
}

/**
 * Reload the page, at most once per window.
 *
 * Returns true if a reload was triggered, false if one was already attempted
 * recently -- the caller uses that to decide whether to show an error instead.
 * The clock and storage are injectable so the loop guard can be tested without
 * actually navigating.
 */
export function reloadOnce({
  storage = window.sessionStorage,
  location = window.location,
  now = () => Date.now(),
} = {}) {
  let last = 0;
  try {
    last = Number(storage.getItem(RELOAD_KEY)) || 0;
  } catch (err) {
    // Storage can throw in private browsing modes. A tab that cannot remember
    // its last attempt must not reload at all, or it would loop unboundedly.
    return false;
  }

  const currentTime = now();
  if (last && currentTime - last < RELOAD_WINDOW_MS) {
    return false;
  }

  try {
    storage.setItem(RELOAD_KEY, String(currentTime));
  } catch (err) {
    return false;
  }

  location.reload();
  return true;
}

/**
 * Listen for chunk failures and recover from them.
 *
 * Two listeners are needed because the failure arrives by two different routes:
 * a rejected dynamic import surfaces as an unhandled promise rejection, while a
 * `<script>`/`<link>` that 404s fires an error event on the element itself.
 * Resource error events do not bubble, hence the capture phase.
 */
export function installChunkErrorReload(target = window) {
  target.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      if (reloadOnce()) {
        // Suppress the console noise only when we are acting on it; an error we
        // are not handling should stay visible.
        event.preventDefault();
      }
    }
  });

  target.addEventListener(
    'error',
    (event) => {
      const element = event.target;

      if (element && (element.tagName === 'SCRIPT' || element.tagName === 'LINK')) {
        const url = element.src || element.href || '';
        // Only our own build output. A third-party script failing is not a
        // stale bundle and reloading would not fix it.
        if (url.includes('/static/')) {
          reloadOnce();
        }
        return;
      }

      if (isChunkLoadError(event.error)) {
        reloadOnce();
      }
    },
    true,
  );
}
